<template>
  <div class="overlay" @click="$emit('close')">
    <div class="pm-dialog" @click.stop>
      <div class="pm-head">
        <div>
          <h3 class="pm-title">Detalle de la solicitud</h3>
          <div class="pm-sub">
            <span class="folio">{{ registro.folio }}</span>
            <PortalBadge :text="registro.area" :color="colorArea(registro.area)" />
          </div>
        </div>
        <button class="icon-btn" @click="$emit('close')">
          <PortalIcon name="X" :size="20" />
        </button>
      </div>

      <div class="form">
        <!-- Datos de solo lectura -->
        <section class="ro-card">
          <div class="ro-row">
            <span class="ro-k">Cliente</span>
            <span class="ro-v">{{ registro.cliente || "—" }}</span>
          </div>
          <div class="ro-row">
            <span class="ro-k">Asunto</span>
            <span class="ro-v">{{ registro.asunto || "—" }}</span>
          </div>
          <div class="ro-grid">
            <div class="ro-row">
              <span class="ro-k">Categoría</span>
              <span class="ro-v">{{ registro.categoria || "—" }}</span>
            </div>
            <div class="ro-row">
              <span class="ro-k">Prioridad</span>
              <span
                class="ro-v prio"
                :style="{ color: PRIORIDAD_COLOR[registro.prioridad] || '#64748b' }"
              >{{ registro.prioridad || "—" }}</span>
            </div>
            <div class="ro-row">
              <span class="ro-k">Canal</span>
              <span class="ro-v">{{ registro.canal || "—" }}</span>
            </div>
            <div class="ro-row">
              <span class="ro-k">Solicitante</span>
              <span class="ro-v">
                {{ registro.solicitante || "—" }}
                <template v-if="registro.solicitanteTipo">
                  · {{ registro.solicitanteTipo }}
                </template>
              </span>
            </div>
            <div class="ro-row">
              <span class="ro-k">Fecha inicial</span>
              <span class="ro-v">{{ fmt(registro.fechaInicial) }}</span>
            </div>
          </div>
        </section>

        <!-- Campos editables -->
        <div class="grid-2">
          <label class="field">
            <span class="field-label">Estatus</span>
            <select
              v-model="e.estatus"
              class="inp"
              :disabled="esGerenteArea"
              :style="{
                color: ESTATUS_COLOR[e.estatus],
                fontWeight: 600,
              }"
              @change="onEstatusChange"
            >
              <option v-for="o in ESTATUS" :key="o" :value="o">{{ o }}</option>
            </select>
          </label>

          <label class="field">
            <span class="field-label">Avance ({{ e.avance }}%)</span>
            <div class="avance-edit">
              <input
                v-model.number="e.avance"
                type="range"
                min="0"
                max="100"
                step="5"
                class="avance-range"
                :disabled="esGerenteArea"
              />
              <input
                v-model.number="e.avance"
                type="number"
                min="0"
                max="100"
                class="inp avance-num"
                :disabled="esGerenteArea"
              />
            </div>
          </label>

          <label class="field">
            <span class="field-label">Fecha compromiso</span>
            <input v-model="e.fechaCompromiso" type="date" class="inp" :disabled="esGerenteArea" />
          </label>

          <label class="field">
            <span class="field-label">Fecha de cierre</span>
            <input v-model="e.fechaFinal" type="date" class="inp" :disabled="esGerenteArea" />
          </label>
        </div>

        <!-- Comentarios (N registros con fecha) -->
        <section class="field">
          <span class="field-label">
            Comentarios
            <span class="cm-count">({{ comentarios.length }})</span>
          </span>

          <ul v-if="comentarios.length" class="cm-list">
            <li v-for="(c, i) in comentarios" :key="i" class="cm-item">
              <div class="cm-body">
                <p class="cm-text">{{ c.texto }}</p>
                <span class="cm-date">
                  <PortalIcon name="Clock" :size="11" />
                  {{ fmt(c.fecha) }}
                </span>
              </div>
              <button
                class="cm-del"
                title="Quitar comentario"
                @click="quitarComentario(i)"
              >
                <PortalIcon name="X" :size="14" />
              </button>
            </li>
          </ul>
          <p v-else class="cm-empty">Sin comentarios registrados.</p>

          <div class="cm-add">
            <textarea
              v-model="nuevoComentario"
              rows="2"
              placeholder="Escribe un comentario…"
              class="inp textarea"
              @keydown.enter.exact.prevent="agregarComentario"
            ></textarea>
            <button
              class="cm-add-btn"
              :disabled="!nuevoComentario.trim()"
              @click="agregarComentario"
            >
              <PortalIcon name="Plus" :size="15" /> Agregar comentario
            </button>
          </div>
        </section>

        <p class="pm-note">
          <PortalIcon name="Info" :size="13" />
          El estatus, la fecha compromiso y el último comentario se guardan en el
          servidor. El avance, la fecha de cierre y los comentarios adicionales
          aún no se persisten (el servicio los aceptará próximamente).
        </p>

        <div class="pm-actions">
          <button class="btn-sec" @click="$emit('close')">Cerrar</button>
          <button class="submit" @click="guardar">Guardar cambios</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PortalIcon from "./PortalIcon.vue";
import PortalBadge from "./PortalBadge.vue";
import {
  ESTATUS,
  ESTATUS_COLOR,
  PRIORIDAD_COLOR,
  AVANCE_ESTATUS,
} from "./portal.constants";
import { fmt, colorArea, avanceDe } from "./portal.helpers";

export default {
  name: "DetalleRegistroModal",
  components: { PortalIcon, PortalBadge },
  props: {
    registro: { type: Object, required: true },
    rol: { type: String, default: "" },
  },
  emits: ["close", "actualizar"],
  computed: {
    esGerenteArea() {
      return this.rol === "gerente_area";
    },
  },
  data() {
    return {
      ESTATUS,
      ESTATUS_COLOR,
      PRIORIDAD_COLOR,
      e: {
        estatus: this.registro.estatus || "En proceso",
        avance: avanceDe(this.registro),
        fechaCompromiso: this.registro.fechaCompromiso || "",
        fechaFinal: this.registro.fechaFinal || "",
      },
      // Lista de comentarios {texto, fecha}. Se inicializa desde la lista
      // estructurada si existe, o del comentario simple heredado del backend.
      comentarios: this.initComentarios(),
      nuevoComentario: "",
    };
  },
  methods: {
    fmt,
    colorArea,
    initComentarios() {
      if (Array.isArray(this.registro.comentariosList)) {
        return this.registro.comentariosList.map((c) => ({ ...c }));
      }
      if (this.registro.comentarios) {
        return [
          { texto: this.registro.comentarios, fecha: this.registro.fechaInicial || "" },
        ];
      }
      return [];
    },
    hoyISO() {
      return new Date().toISOString().slice(0, 10);
    },
    // Al cambiar el estatus, sugiere el avance típico de ese estatus.
    onEstatusChange() {
      const sugerido = AVANCE_ESTATUS[this.e.estatus];
      if (sugerido != null) this.e.avance = sugerido;
      if (this.e.estatus === "Concluido" && !this.e.fechaFinal) {
        this.e.fechaFinal = this.hoyISO();
      }
    },
    agregarComentario() {
      const texto = this.nuevoComentario.trim();
      if (!texto) return;
      this.comentarios.push({ texto, fecha: this.hoyISO() });
      this.nuevoComentario = "";
    },
    quitarComentario(i) {
      this.comentarios.splice(i, 1);
    },
    guardar() {
      const av = Math.max(0, Math.min(100, Number(this.e.avance) || 0));
      this.$emit("actualizar", {
        id: this.registro.id,
        estatus: this.e.estatus,
        avance: av,
        fechaCompromiso: this.e.fechaCompromiso || null,
        fechaFinal: this.e.fechaFinal || null,
        comentariosList: this.comentarios.map((c) => ({ ...c })),
        // Compatibilidad con el campo simple del backend (último comentario).
        comentarios: this.comentarios.length
          ? this.comentarios[this.comentarios.length - 1].texto
          : "",
      });
    },
  },
};
</script>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(15, 23, 42, 0.45);
}
.pm-dialog {
  width: 100%;
  max-width: 560px;
  max-height: 100%;
  overflow-y: auto;
  background: var(--pt-card);
  border-radius: 16px;
  padding: 20px;
}
.pm-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}
.pm-title {
  font-size: 18px;
  font-weight: 700;
  margin: 0;
}
.pm-sub {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
}
.folio {
  font-size: 13px;
  font-weight: 600;
  color: var(--pt-dim);
  font-variant-numeric: tabular-nums;
}
.icon-btn {
  background: none;
  border: 0;
  cursor: pointer;
  color: var(--pt-muted);
}
.form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

/* Datos de solo lectura */
.ro-card {
  background: var(--pt-elev);
  border: 1px solid var(--pt-border);
  border-radius: 12px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.ro-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.ro-row {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.ro-k {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--pt-muted);
}
.ro-v {
  font-size: 14px;
  color: var(--pt-text);
}
.ro-v.prio {
  font-weight: 600;
}

/* Campos editables (reutiliza estilo del modal de alta) */
.grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.field {
  display: block;
}
.field-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: var(--pt-dim);
  margin-bottom: 4px;
}
.inp {
  width: 100%;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 14px;
  border: 1px solid var(--pt-border);
  outline: none;
  background: var(--pt-card);
  color: var(--pt-text);
  box-sizing: border-box;
  font-family: inherit;
}
.inp:focus {
  border-color: #003ca5;
}
.textarea {
  resize: vertical;
}
.avance-edit {
  display: flex;
  align-items: center;
  gap: 10px;
}
.avance-range {
  flex: 1;
  accent-color: #003ca5;
}
.avance-num {
  width: 68px;
  flex-shrink: 0;
}

/* Comentarios */
.cm-count {
  color: var(--pt-muted);
  font-weight: 500;
}
.cm-list {
  list-style: none;
  margin: 0 0 10px;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.cm-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: var(--pt-elev);
  border: 1px solid var(--pt-border);
  border-radius: 10px;
  padding: 8px 10px;
}
.cm-body {
  flex: 1;
  min-width: 0;
}
.cm-text {
  margin: 0;
  font-size: 14px;
  color: var(--pt-text);
  white-space: pre-wrap;
  word-break: break-word;
}
.cm-date {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  font-size: 11px;
  color: var(--pt-muted);
}
.cm-del {
  background: none;
  border: 0;
  cursor: pointer;
  color: var(--pt-muted);
  flex-shrink: 0;
}
.cm-del:hover {
  color: #dc2626;
}
.cm-empty {
  margin: 0 0 10px;
  font-size: 13px;
  color: var(--pt-muted);
}
.cm-add {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.cm-add-btn {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  background: #e7eefb;
  color: #003ca5;
  border: 0;
}
.cm-add-btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

/* Nota + acciones */
.pm-note {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 0;
  font-size: 12px;
  color: var(--pt-muted);
}
.pm-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 4px;
}
.btn-sec {
  padding: 10px 18px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background: var(--pt-elev);
  color: var(--pt-dim);
  border: 1px solid var(--pt-border);
}
.submit {
  padding: 10px 18px;
  border: 0;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  background: #003ca5;
  cursor: pointer;
}

@media (max-width: 520px) {
  .ro-grid,
  .grid-2 {
    grid-template-columns: 1fr;
  }
}
</style>
